import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import FileUpload from '../../../components/ui/FileUpload';
import SignatureUploadGuide from '../../../components/ui/SignatureUploadGuide';
import { generateAndDownloadNDA, generateAndDownloadSQA, generateAndDownload4MDeclaration, generateAndDownloadCodeOfConduct, generateAndDownloadComplianceAgreement, generateAndDownloadSelfDeclaration, generateAndDownloadOverseasAgreement } from '../../../utils/documentGenerator.jsx';
import { isIndia } from '../../../utils/countries.js';

const AgreementStep = ({ formData, updateFormData, errors, onGeneratePDF, onPreviewDocument, keepExistingDocuments, setKeepExistingDocuments, uploadedFiles }) => {
  const [signaturePreview, setSignaturePreview] = useState(null);

  // Helper function to check if supplier is ODM
  const isODMSupplier = () => {
    return formData.supplierGroup === 'odm-amber';
  };

  // Helper function to check if supplier is Indian
  const isIndianSupplier = () => {
    return isIndia(formData.countryOrigin);
  };

  console.log(formData);

  // Generate preview for uploaded signature
  useEffect(() => {
    if (formData.digitalSignature && formData.digitalSignature instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(formData.digitalSignature);
    } else if (!formData.digitalSignature) {
      setSignaturePreview(null);
    }
  }, [formData.digitalSignature]);

  const agreements = [
    {
      id: 'nda',
      title: 'Non-Disclosure Agreement (NDA)',
      description: `I agree to enter into the Mutual Confidentiality and Non-Disclosure Agreement with PICL (India) Pvt Ltd.\n\nUnder this agreement both parties:\n• Use Confidential Information only for the agreed Purpose\n• Share it only with employees and advisors on a need-to-know basis\n• Return or destroy it on request or on expiry\n• Keep it confidential for the 1-year term and 1 year after\n• Resolve disputes by arbitration seated in Delhi`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadNDA(formData),
      shouldShow: () => !isODMSupplier() // Show when NOT ODM
    },
    {
      id: 'sqa',
      title: 'Supplier Agreement',
      description: `The Supplier agrees to uphold the highest standards in the performance of its obligations under this Agreement.\n\n This includes, without limitation\n• Compliance with all specifications, requirements, and guidelines\n• Implementation of appropriate processes and controls to ensure consistent performance.\n• Engaging in continuous improvement initiatives to enhance efficiency, reliability.\n• Immediate notification of quality issues\n• Continuous improvement initiatives`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadSQA(formData),
      shouldShow: () => isIndianSupplier() // Show when Indian supplier
    },
    {
      id: 'fourM',
      title: '4M Change Control Agreement',
      description: `I agree to notify PICL of any changes in the 4M parameters:\n\n• Man (Personnel changes)\n• Machine (Equipment modifications)\n• Material (Raw material changes)\n• Method (Process modifications)\n\nAll changes must be approved before implementation.`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownload4MDeclaration(formData),
      shouldShow: () => true // Always show
    },
    {
      id: 'codeOfConduct',
      title: 'Code of Conduct for Business Partners',
      shortName: 'Code of Conduct',
      description: `I agree to adhere to the PICL Code of Conduct for Business Partners (Ver. 1, 2nd Sep 2026).\n\nThis covers:\n• Labor and human rights, health, safety and environment\n• Anti-bribery, anti-corruption, conflicts of interest and fair competition\n• Gifts and hospitality limits, protection of information and data privacy\n• Anti-counterfeit measures, no retaliation and insider trading\n• Audit, indemnification and consequences of violation`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadCodeOfConduct(formData),
      shouldShow: () => true // Always show
    },
    {
      id: 'complianceAgreement',
      title: 'Compliance Agreement',
      description: `I confirm compliance with all applicable laws, regulations, and industry standards.\n\nThis includes:\n• Local and international trade laws\n• Environmental regulations\n• Labor laws and worker rights\n• Tax and financial regulations\n• Industry-specific compliance requirements`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadComplianceAgreement(formData),
      shouldShow: () => isIndianSupplier() // Show when Indian supplier
    },
    {
      id: 'selfDeclaration',
      title: 'Self Declaration',
      description: `I hereby declare that:\n\n• All information provided in this registration is true and accurate\n• I have the authority to enter into this agreement\n• My organization has the capability to fulfill the requirements\n• I will promptly notify of any material changes\n• I understand the consequences of providing false information`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadSelfDeclaration(formData),
      shouldShow: () => true // Always show
    },
    {
      id: 'overseasAgreement',
      title: 'Overseas Agreement',
      description: `I agree to the terms and conditions specified in the Overseas Agreement for foreign suppliers.\n\nThis agreement covers:\n• Terms and conditions for overseas suppliers\n• Compliance with international trade regulations\n• Quality and delivery commitments\n• Legal and regulatory requirements`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadOverseasAgreement(formData),
      shouldShow: () => !isIndianSupplier() // Show only for foreign vendors
    }
  ];

  // Filter agreements based on display constraints
  const visibleAgreements = agreements.filter(agreement => agreement.shouldShow());

  const handleAgreementChange = (agreementId, checked) => {
    const currentAgreements = formData.agreements || {};
    updateFormData({
      agreements: {
        ...currentAgreements,
        [agreementId]: checked
      }
    });
  };

  const handleGeneratePDF = (agreement) => {
    if (agreement.generateFunction) {
      agreement.generateFunction();
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">Agreements & Declarations</h2>
        <p className="text-text-secondary">
          Please review and accept the following agreements to complete your vendor registration.
        </p>
      </div>

      <div className="space-y-6">
        {visibleAgreements.map((agreement) => (
          <div key={agreement.id} className="bg-muted/30 p-6 rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {agreement.title}
                  {agreement.required && <span className="text-red-500 ml-1">*</span>}
                </h3>
                <p className="text-sm text-text-secondary whitespace-pre-line">
                  {agreement.description}
                </p>
              </div>
              <div className="ml-4 flex items-center space-x-3">
                <Checkbox
                  id={agreement.id}
                  checked={formData.agreements?.[agreement.id] || false}
                  onCheckedChange={(checked) => handleAgreementChange(agreement.id, checked)}
                  required={agreement.required}
                />
                {agreement.canGenerate && formData.agreements?.[agreement.id] && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleGeneratePDF(agreement)}
                    className="flex items-center space-x-2"
                  >
                    <Icon name="Download" className="w-4 h-4" />
                    <span>Generate {agreement.shortName || agreement.title.split(' ')[0]}</span>
                  </Button>
                )}
              </div>
            </div>
            {errors[`agreements.${agreement.id}`] && (
              <p className="text-sm text-red-500 mt-2">{errors[`agreements.${agreement.id}`]}</p>
            )}
          </div>
        ))}
      </div>

      {/* Digital Signature Section */}
      <div className="mt-8 pt-6 border-t border-border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center">
            <Icon name="FileSignature" className="w-6 h-6 mr-2 text-blue-600" />
            Digital Signature
            <span className="text-red-500 ml-1">*</span>
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Upload your authorized signatory's digital signature. This signature will appear on all vendor agreements including NDA, SQA, 4M Change Control, Code of Conduct, Compliance Agreement, Overseas Agreement, and Self Declaration.
          </p>

          {/* Signature Upload Guide */}
          <SignatureUploadGuide />
        </div>

        <div className="mt-6 space-y-4">
          {/* Show checkbox if document is pre-filled */}
          {uploadedFiles?.DIGITAL_SIGNATURE && !formData.digitalSignature?.name && (
            <div className="mb-4 p-3 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="keepDigitalSignature"
                    checked={keepExistingDocuments.digitalSignature || false}
                    onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, digitalSignature: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="keepDigitalSignature" className="text-sm text-foreground cursor-pointer">
                    Keep existing: <span className="font-medium">{uploadedFiles.DIGITAL_SIGNATURE.file_name}</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.DIGITAL_SIGNATURE, 'DIGITAL_SIGNATURE')}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center space-x-1"
                >
                  <Icon name="eye" className="w-4 h-4" />
                  <span>Preview</span>
                </button>
              </div>
            </div>
          )}

          {/* Only show upload field if not keeping existing */}
          {(!keepExistingDocuments.digitalSignature || !uploadedFiles?.DIGITAL_SIGNATURE) && (
            <FileUpload
            label="Upload Digital Signature"
            accept=".jpg,.jpeg,.png"
            maxSize={10 * 1024}
            value={formData.digitalSignature}
            onChange={(file) => updateFormData({ digitalSignature: file })}
            onRemove={() => updateFormData({ digitalSignature: null })}  // ← ADD THIS LINE
            onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'DIGITAL_SIGNATURE')}
            error={errors.digitalSignature}
            description="JPG or PNG format • Max 10KB • Recommended: 400×200px with transparent background"
            required
          />
          )}

          {/* Signature Preview */}
          {signaturePreview && (
            <div className="bg-muted/30 rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-foreground">Signature Preview</h4>
                <span className="text-xs text-green-600 flex items-center">
                  <Icon name="CheckCircle2" className="w-4 h-4 mr-1" />
                  Uploaded Successfully
                </span>
              </div>
              <div className="bg-white rounded border border-gray-200 p-4 flex items-center justify-center min-h-[120px]">
                <img
                  src={signaturePreview}
                  alt="Digital Signature Preview"
                  className="max-h-[80px] max-w-full object-contain"
                  style={{ imageRendering: 'high-quality' }}
                />
              </div>
              <p className="text-xs text-text-secondary mt-2 text-center">
                This is how your signature will appear on all agreements
              </p>
            </div>
          )}

          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Important:</p>
                <ul className="space-y-1 list-disc ml-4">
                  <li>The uploaded signature must be that of an authorized signatory</li>
                  <li>This signature will be legally binding on all agreements</li>
                  <li>Ensure the signature is clear, professional, and properly formatted</li>
                  <li>Once submitted, you cannot change the signature without admin approval</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Agreement Rules box removed as per requirements */}
    </div>
  );
};

export default AgreementStep;